import { action, makeObservable, observable } from "mobx";
import moment, { Moment } from "moment";

export enum DateReferenceOption {
    ArrivalDate = "ArrivalDate",
    InteractionDate = "InteractionDate",
    EvaluationCompleteDate = "EvaluationCompleteDate",
    EvaluationStartDate = "EvaluationStartDate",
    CreatedOnDate = "CreatedOnDate",
}

export enum InteractionDateReferenceOption {
    ArrivalDate = "ArrivalDate",
    InteractionDate = "InteractionDate",
    EvaluationCompleteDate = "EvaluationCompleteDate",
    EvaluationModifiedDate = "EvaluationModifiedDate",
    EvaluationStartDate = "EvaluationStartDate",
    CreatedOnDate = "CreatedOnDate",
}

export const basicDateReferenceOptions = [
    DateReferenceOption.InteractionDate,
    DateReferenceOption.ArrivalDate,
];

export class DatePickerComponentStore {
    @observable beginDate: moment.Moment;
    @observable offsetBeginDate: moment.Moment;

    @observable endDate: moment.Moment;
    @observable offsetEndDate: moment.Moment;

    @observable referenceOption: DateReferenceOption;
    @observable dateRangeFilterLabel: string = "Interaction Date";
    @observable dateRangePresetLabel: DateRangeLabel = "";

    private readonly initialBeginDate: moment.Moment;
    private readonly initialEndDate: moment.Moment;

    public constructor(
        defaultBeginDate: moment.Moment = moment()
            .startOf("date")
            .subtract(10, "days"),
        defaultEndDate: moment.Moment = moment().endOf("date"),
        defaultReferenceOption: DateReferenceOption = DateReferenceOption.ArrivalDate,
    ) {
        makeObservable(this);
        this.initialBeginDate = defaultBeginDate;
        this.initialEndDate = defaultEndDate;

        this.beginDate = defaultBeginDate;
        this.offsetBeginDate = defaultBeginDate;

        this.endDate = defaultEndDate;
        this.offsetEndDate = defaultEndDate;

        this.referenceOption = defaultReferenceOption;
    }

    /** Restores beginDate/endDate to this store's original defaults, e.g. when a date filter is cleared. */
    @action reset() {
        this.setBeginDate(this.initialBeginDate.clone());
        this.setEndDate(this.initialEndDate.clone());
        this.dateRangePresetLabel = "";
    }

    getBeginDate(useStartOfDay = true) {
        return this.beginDate.startOf("day").utc();
    }
    getOffsetBeginDate() {
        return addLocalOffset(this.beginDate.startOf("day"));
    }
    @action setBeginDate(newDate: moment.Moment, useStartOfDay = false) {
        // if (useStartOfDay) {
        //     this.beginDate = newDate.startOf("date");
        // } else {
        //     this.beginDate = newDate;
        // }
        this.beginDate = newDate;
        this.offsetBeginDate = addLocalOffset(this.beginDate);
    }

    getEndDate() {
        return this.endDate.endOf("day").utc();
    }
    getOffsetEndDate() {
        return addLocalOffset(this.endDate.endOf("day"));
    }
    @action setEndDate(newDate: moment.Moment, useEndOfDay = true) {
        // if (useEndOfDay) {
        //     this.endDate = newDate.endOf("date");
        // } else {
        //     this.endDate = newDate;
        // }
        if (
            this.beginDate &&
            moment(this.beginDate).startOf("date") >
                moment(newDate).startOf("date")
        ) {
            this.endDate = moment(this.beginDate).endOf("date");
            this.setBeginDate(newDate.startOf("date"));
        } else {
            this.endDate = newDate.endOf("date");
        }
        this.offsetEndDate = addLocalOffset(this.endDate);
    }
    @action setReferenceOption(newOption: DateReferenceOption) {
        this.referenceOption = newOption;
        this.dateRangeFilterLabel =
            newOption === "ArrivalDate" ? "Arrived On" : "Interaction Date";
    }

    @action setDateRangePreset(input: DateRangeLabel) {
        this.dateRangePresetLabel = input;
    }
}

function addLocalOffset(inputDate: moment.Moment) {
    const minuteOffset = new Date().getTimezoneOffset();

    return moment(inputDate).add(minuteOffset, "minutes");
}

export type DateRangeLabel =
    | "Last 7 Days"
    | "Last 30 Days"
    | "This Week"
    | "Last Week"
    | "This Month"
    | "Last Month"
    | "This Quarter"
    | "Last Quarter"
    | "";

/**
 * Returns moment objects in an array [startDate, endDate]
 * @param {string} value - A DateRangeLabel string
 */
export function getDateFromDateRangeLabel(value: DateRangeLabel): Moment[] {
    let startDate, endDate;
    switch (value) {
        case "Last 7 Days":
            startDate = moment().subtract(7, "days").startOf("day");
            endDate = moment();
            break;
        case "Last 30 Days":
            startDate = moment().subtract(30, "days").startOf("day");
            endDate = moment();
            break;
        case "This Week":
            startDate = moment().startOf("week");
            endDate = moment().endOf("week");
            break;
        case "Last Week":
            startDate = moment().subtract(1, "week").startOf("week");
            endDate = moment().subtract(1, "week").endOf("week");
            break;
        case "This Month":
            startDate = moment().startOf("month");
            endDate = moment().endOf("month");
            break;
        case "Last Month":
            startDate = moment().subtract(1, "month").startOf("month");
            endDate = moment().subtract(1, "month").endOf("month");
            break;
        case "This Quarter":
            startDate = moment().startOf("quarter");
            endDate = moment().endOf("quarter");
            break;
        case "Last Quarter":
            startDate = moment().subtract(1, "quarter").startOf("quarter");
            endDate = moment().subtract(1, "quarter").endOf("quarter");
            break;
        default:
            startDate = moment();
            endDate = moment();
    }
    return [startDate, endDate];
}
